import { useContext, useEffect } from 'react';
import { ConfirmationDialog } from 'components/Dialogs';
import { ConfirmUnsavedChanges } from 'components/Spreadsheet/components';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import type { DialogAction } from 'providers/UnsavedChangesProvider/UnsavedChangesProvider.props';
import { useCellAnnotationStore } from 'store/useCellAnnotationStore';
import type { CellAnnotationProviderProps } from './CellAnnotationProvider.props';

// Wraps a host page that uses CellAnnotationValueViewer. Publishes annotation
// dirtiness into the page's UnsavedChangesContext so attempted navigation
// triggers the standard confirm-leave dialog. Store hygiene across captables
// is handled by useHydrateCaptableCellAnnotations, which fully replaces the
// states map on every captable load — no unmount cleanup needed here.
//
// Each popup builds its own SpreadsheetConfig + useWorkbook locally (see
// CellAnnotationPopupSheet), so this provider does not need to plumb a
// shared workbook through context — it only owns the unsaved-changes wiring.
const CellAnnotationProvider: React.FC<CellAnnotationProviderProps> = ({ children }) => {
  const isAnyDirty = useCellAnnotationStore(store => store.isAnyDirty());
  const { setAction } = useContext(UnsavedChangesContext);

  useEffect(() => {
    if (isAnyDirty) {
      setAction({
        wrapper: ConfirmationDialog,
        content: ConfirmUnsavedChanges,
      } as unknown as DialogAction);
    } else {
      setAction(false);
    }
  }, [isAnyDirty, setAction]);

  // Intentionally no reset-on-unmount: the save flow causes a transient early
  // return in the host page which would unmount-remount this provider and wipe
  // a store that was just hydrated with the server's saved values. Store
  // hygiene across captables is already guaranteed by useHydrateCaptableCell-
  // Annotations, which replaces the entire `states` map on every captable load.
  return <>{children}</>;
};

export default CellAnnotationProvider;
